import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { isProd } from "../../helpers/env.helpers";
import {
  PAUSE_POLLING_WHEN_UNFOCUSED,
  SURVEY_RESULTS_SUMMARY_REFRESH_INTERVAL_MS,
} from "src/constants/polling.constants";
import { useGetSurveyQuery, useGetSurveyStatsQuery, useGetSurveySummaryQuery } from "../../redux/survey/survey.endpoints";
import { SurveyFull, SurveyStats, SurveyStatus, SurveySummaryData } from "../../redux/survey/survey.types";
import FirebaseService from "../../services/firebase/firebase.service";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/redux/redux-store";
import { clearSelectedAnswers } from "src/redux/survey/survey-selected-answers.slice";

const { Complete } = SurveyStatus;

interface UseViewSurveyDataReturn {
  readonly isShare: boolean,
  readonly shareId?: string,
  readonly showSummaries: boolean,
  readonly stats?: SurveyStats,
  readonly survey?: SurveyFull,
  readonly surveyId: string;
  readonly surveyError?: boolean,
  readonly summary?: SurveySummaryData,
}

export const useViewSurveyData = (): UseViewSurveyDataReturn => {
  const dispatch = useDispatch();
  const selectedAnswerIds = useSelector((state: RootState) => {
    return state.selectedAnswers.selectedAnswerIds;
  });
  const refreshConfig = FirebaseService.getRemoteConfigNumber('RESULTS_AUTO_REFRESH_SECONDS') || 0;

  const { extSurveyId } = useParams<{ extSurveyId?: string }>();
  const { shareId } = useParams<{ shareId?: string }>();
  const [searchParams] = useSearchParams();
  /** Legacy `/view-survey?ext-survey-id=` — ID is only in the query string. */
  const extSurveyIdFromQuery = searchParams.get("ext-survey-id") || undefined;

  const surveyId = extSurveyId ?? shareId ?? extSurveyIdFromQuery;
  const isShare = Boolean(shareId);

  // Clear selected answers when survey changes
  useEffect(() => {
    if (surveyId) {
      dispatch(clearSelectedAnswers());
    }
  }, [surveyId, dispatch]);

  const fetchParams = { ext_survey_id: surveyId ?? '', share: isShare, selected_survey_answers: selectedAnswerIds };
  const [statsRefreshInterval, setStatsRefreshInterval] = useState<number | undefined>(undefined);
  const surveyFetchOptions = {
    skip: !surveyId,
    refetchOnMountOrArgChange: true,
    pollingInterval: statsRefreshInterval,
    ...PAUSE_POLLING_WHEN_UNFOCUSED,
  };
  const { data: survey, isError: surveyError } = useGetSurveyQuery(fetchParams, surveyFetchOptions);

  const { data: statsFromApi } = useGetSurveyStatsQuery(fetchParams, {
    ...surveyFetchOptions,
    skip: !surveyId,
  });

  const hasFetchedAfterComplete = useRef(false);
  const [summaryRefreshInterval, setSummaryRefreshInterval] = useState<number | undefined>(undefined);
  const showSummaries = survey?.surveyStatus === SurveyStatus.Complete || survey?.surveyStatus === SurveyStatus.Live;

  const { data: summaryFromApi } = useGetSurveySummaryQuery(fetchParams, {
    pollingInterval: summaryRefreshInterval,
    skip: !surveyId || !showSummaries || !summaryRefreshInterval,
    ...PAUSE_POLLING_WHEN_UNFOCUSED,
  });

  const stats = statsFromApi;

  const summary = summaryFromApi;

  useEffect(() => {
    // On QA or Perf, most surveys are not answered by real users
    // so we should stop refreshing after AI finishes
    let blockedByAICompletion = false;
    if (!isProd() && survey && (survey.maxAICompletes !== null || survey.maxCompletes !== 0) && stats) {
      const completed = stats.total_users?.completed ?? stats.totalUsers ?? 0;
      const maxCompletes = survey.maxAICompletes ?? survey.maxCompletes ?? 0;
      blockedByAICompletion = maxCompletes > 0 && completed >= maxCompletes;
    }

    const shouldRefreshAsComplete = survey?.surveyStatus === Complete && !hasFetchedAfterComplete.current;
    const shouldRefresh = Boolean(refreshConfig && (survey?.surveyStatus === SurveyStatus.Live || shouldRefreshAsComplete) && !blockedByAICompletion);
    if (shouldRefresh && shouldRefreshAsComplete) {
      hasFetchedAfterComplete.current = true;
    }
    setStatsRefreshInterval(shouldRefresh ? refreshConfig * 1000 : undefined);
  }, [refreshConfig, survey, stats]);

  useEffect(() => {
    showSummaries && setSummaryRefreshInterval(summary ? undefined : SURVEY_RESULTS_SUMMARY_REFRESH_INTERVAL_MS);
  }, [summary, showSummaries]);

  return {
    isShare,
    showSummaries,
    stats,
    survey,
    surveyError,
    summary,
    surveyId: surveyId || '',
  };
};
